/**
 * useACF Hook
 *
 * TanStack Query hooks for AI CodeForge session management
 */

import * as React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

/**
 * ACF Session Status
 */
export type ACFSessionStatus = 'active' | 'paused' | 'completed' | 'archived';

/**
 * ACF Message Role
 */
export type ACFMessageRole = 'user' | 'assistant' | 'system';

/**
 * ACF Session Settings
 */
export interface ACFSessionSettings {
  model?: string;
  temperature?: number;
  maxTokens?: number;
  systemPrompt?: string;
}

/**
 * ACF Session Response
 */
export interface ACFSession {
  id: string;
  workspaceId: string;
  userId: string;
  repositoryId?: string;
  title?: string;
  status: ACFSessionStatus;
  settings: ACFSessionSettings;
  contextSummary?: string;
  totalTokensUsed: number;
  createdAt: string;
  updatedAt: string;
}

/**
 * ACF Message Response
 */
export interface ACFMessage {
  id: string;
  sessionId: string;
  role: ACFMessageRole;
  content: string;
  model?: string;
  tokenCount?: number;
  metadata?: Record<string, unknown>;
  createdAt: string;
}

/**
 * ACF Context Item
 */
export interface ACFContextItem {
  id: string;
  sessionId: string;
  type: 'file' | 'snippet' | 'url' | 'reference';
  content: string;
  path?: string;
  relevanceScore?: number;
  createdAt: string;
}

/**
 * Create Session Request
 */
export interface CreateSessionRequest {
  workspaceId: string;
  repositoryId?: string;
  title?: string;
  settings?: ACFSessionSettings;
}

/**
 * Update Session Request
 */
export interface UpdateSessionRequest {
  title?: string;
  status?: ACFSessionStatus;
  settings?: ACFSessionSettings;
  contextSummary?: string;
}

/**
 * Create Message Request
 */
export interface CreateMessageRequest {
  role: ACFMessageRole;
  content: string;
  model?: string;
  metadata?: Record<string, unknown>;
}

/**
 * Add Context Item Request
 */
export interface AddContextItemRequest {
  type: 'file' | 'snippet' | 'url' | 'reference';
  content: string;
  path?: string;
  relevanceScore?: number;
}

/**
 * Stream Chunk Types
 */
export interface StreamChunk {
  type: 'content' | 'metadata' | 'done' | 'error';
  content?: string;
  error?: string;
  usage?: {
    completionTokens?: number;
    totalTokens?: number;
  };
}

// Query Keys
const acfKeys = {
  all: ['acf'] as const,
  sessions: (workspaceId: string) => [...acfKeys.all, 'sessions', workspaceId] as const,
  session: (sessionId: string) => [...acfKeys.all, 'session', sessionId] as const,
  messages: (sessionId: string) => [...acfKeys.all, 'messages', sessionId] as const,
  context: (sessionId: string) => [...acfKeys.all, 'context', sessionId] as const,
};

/**
 * Hook to get ACF sessions for a workspace
 */
export function useACFSessions(workspaceId: string, enabled = true) {
  return useQuery({
    queryKey: acfKeys.sessions(workspaceId),
    queryFn: async () => {
      const response = await api.get<{ sessions: ACFSession[]; count: number }>(
        `/acf/sessions?workspaceId=${workspaceId}`
      );
      return response;
    },
    enabled: enabled && !!workspaceId,
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Hook to get a single ACF session
 */
export function useACFSession(sessionId: string, enabled = true) {
  return useQuery({
    queryKey: acfKeys.session(sessionId),
    queryFn: async () => {
      const response = await api.get<{ session: ACFSession }>(`/acf/sessions/${sessionId}`);
      return response.session;
    },
    enabled: enabled && !!sessionId,
    staleTime: 30 * 1000,
  });
}

/**
 * Hook to create a new ACF session
 */
export function useCreateACFSession() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateSessionRequest) => {
      const response = await api.post<{ session: ACFSession }>('/acf/sessions', data);
      return response.session;
    },
    onSuccess: (session) => {
      // Invalidate sessions list
      queryClient.invalidateQueries({
        queryKey: acfKeys.sessions(session.workspaceId),
      });
    },
  });
}

/**
 * Hook to update an ACF session
 */
export function useUpdateACFSession() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ sessionId, updates }: { sessionId: string; updates: UpdateSessionRequest }) => {
      const response = await api.patch<{ session: ACFSession }>(
        `/acf/sessions/${sessionId}`,
        updates
      );
      return response.session;
    },
    onSuccess: (session) => {
      // Update cached session
      queryClient.setQueryData(acfKeys.session(session.id), session);
      // Invalidate sessions list
      queryClient.invalidateQueries({
        queryKey: acfKeys.sessions(session.workspaceId),
      });
    },
  });
}

/**
 * Hook to delete an ACF session
 */
export function useDeleteACFSession() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ sessionId, workspaceId }: { sessionId: string; workspaceId: string }) => {
      await api.delete(`/acf/sessions/${sessionId}`);
      return { sessionId, workspaceId };
    },
    onSuccess: ({ sessionId, workspaceId }) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: acfKeys.session(sessionId) });
      queryClient.removeQueries({ queryKey: acfKeys.messages(sessionId) });
      queryClient.removeQueries({ queryKey: acfKeys.context(sessionId) });
      // Invalidate sessions list
      queryClient.invalidateQueries({
        queryKey: acfKeys.sessions(workspaceId),
      });
    },
  });
}

/**
 * Hook to get messages for a session
 */
export function useACFMessages(sessionId: string, enabled = true) {
  return useQuery({
    queryKey: acfKeys.messages(sessionId),
    queryFn: async () => {
      const response = await api.get<{ messages: ACFMessage[]; count: number }>(
        `/acf/sessions/${sessionId}/messages`
      );
      return response.messages;
    },
    enabled: enabled && !!sessionId,
    staleTime: 10 * 1000, // 10 seconds
  });
}

/**
 * Hook to send a message to a session
 */
export function useSendACFMessage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      sessionId,
      message,
    }: {
      sessionId: string;
      message: CreateMessageRequest;
    }) => {
      const response = await api.post<{ message: ACFMessage }>(
        `/acf/sessions/${sessionId}/messages`,
        message
      );
      return response.message;
    },
    onSuccess: (message) => {
      // Add message to cache
      queryClient.setQueryData<ACFMessage[]>(
        acfKeys.messages(message.sessionId),
        (old) => (old ? [...old, message] : [message])
      );
    },
  });
}

/**
 * Hook to get context items for a session
 */
export function useACFContext(sessionId: string, enabled = true) {
  return useQuery({
    queryKey: acfKeys.context(sessionId),
    queryFn: async () => {
      const response = await api.get<{ items: ACFContextItem[]; count: number }>(
        `/acf/sessions/${sessionId}/context`
      );
      return response.items;
    },
    enabled: enabled && !!sessionId,
    staleTime: 30 * 1000,
  });
}

/**
 * Hook to add context item to a session
 */
export function useAddACFContext() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      sessionId,
      item,
    }: {
      sessionId: string;
      item: AddContextItemRequest;
    }) => {
      const response = await api.post<{ item: ACFContextItem }>(
        `/acf/sessions/${sessionId}/context`,
        item
      );
      return response.item;
    },
    onSuccess: (item) => {
      // Add to cache
      queryClient.setQueryData<ACFContextItem[]>(
        acfKeys.context(item.sessionId),
        (old) => (old ? [...old, item] : [item])
      );
    },
  });
}

/**
 * Hook to remove context item from a session
 */
export function useRemoveACFContext() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ sessionId, itemId }: { sessionId: string; itemId: string }) => {
      await api.delete(`/acf/sessions/${sessionId}/context/${itemId}`);
      return { sessionId, itemId };
    },
    onSuccess: ({ sessionId, itemId }) => {
      // Remove from cache
      queryClient.setQueryData<ACFContextItem[]>(
        acfKeys.context(sessionId),
        (old) => (old ? old.filter((i) => i.id !== itemId) : [])
      );
    },
  });
}

/**
 * Custom hook for SSE streaming chat
 *
 * This hook manages the streaming response from the AI
 */
export function useACFStream() {
  const [isStreaming, setIsStreaming] = React.useState(false);
  const [streamContent, setStreamContent] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const abortControllerRef = React.useRef<AbortController | null>(null);
  const queryClient = useQueryClient();

  const startStream = React.useCallback(
    async (
      sessionId: string,
      userMessage: string,
      options?: {
        model?: string;
        systemPrompt?: string;
        onChunk?: (chunk: StreamChunk) => void;
        onComplete?: (fullContent: string) => void;
        onError?: (error: string) => void;
      }
    ) => {
      setIsStreaming(true);
      setStreamContent('');
      setError(null);

      // Create abort controller for cancellation
      abortControllerRef.current = new AbortController();

      try {
        // First, add the user message
        await api.post(`/acf/sessions/${sessionId}/messages`, {
          role: 'user',
          content: userMessage,
        });

        // Invalidate messages to reflect the user message
        queryClient.invalidateQueries({
          queryKey: acfKeys.messages(sessionId),
        });

        // Start SSE stream with cookie-based auth
        const response = await fetch(`/api/v1/acf/sessions/${sessionId}/stream`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            model: options?.model,
            systemPrompt: options?.systemPrompt,
          }),
          signal: abortControllerRef.current.signal,
        });

        if (!response.ok) {
          throw new Error(`Stream request failed: ${response.statusText}`);
        }

        if (!response.body) {
          throw new Error('No response body');
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let fullContent = '';
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();

          if (done) {
            break;
          }

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            const trimmed = line.trim();

            if (!trimmed || trimmed === 'data: [DONE]') {
              continue;
            }

            if (trimmed.startsWith('data: ')) {
              const jsonStr = trimmed.slice(6);

              try {
                const chunk = JSON.parse(jsonStr) as StreamChunk;

                if (chunk.type === 'content' && chunk.content) {
                  fullContent += chunk.content;
                  setStreamContent(fullContent);
                  options?.onChunk?.(chunk);
                } else if (chunk.type === 'error') {
                  setError(chunk.error || 'Stream error');
                  options?.onError?.(chunk.error || 'Stream error');
                } else if (chunk.type === 'done') {
                  options?.onComplete?.(fullContent);
                }
              } catch {
                // Ignore parse errors for incomplete chunks
              }
            }
          }
        }

        // Add assistant message to cache
        queryClient.invalidateQueries({
          queryKey: acfKeys.messages(sessionId),
        });

        setIsStreaming(false);
        return fullContent;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Stream failed';
        setError(errorMessage);
        setIsStreaming(false);
        options?.onError?.(errorMessage);
        throw err;
      }
    },
    [queryClient]
  );

  const stopStream = React.useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      setIsStreaming(false);
    }
  }, []);

  const resetStream = React.useCallback(() => {
    setStreamContent('');
    setError(null);
  }, []);

  return {
    isStreaming,
    streamContent,
    error,
    startStream,
    stopStream,
    resetStream,
  };
}

/**
 * Hook to export a conversation
 */
export function useExportACFConversation() {
  return useMutation({
    mutationFn: async ({
      sessionId,
      format = 'markdown',
    }: {
      sessionId: string;
      format?: 'json' | 'markdown';
    }) => {
      const response = await api.get<{ format: string; conversation: string }>(
        `/acf/sessions/${sessionId}/export?format=${format}`
      );
      return response;
    },
  });
}
